import Container from '@/components/ui/container'
import InputUi from '@/components/ui/inputui'
import React, { useState, useEffect, useCallback } from 'react'
import Layout from 'src/layout'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useParams, useNavigate } from 'react-router-dom'
import { getOrderDetails, updateOrderStatus, updatePaymentStatus, downloadInvoice, emailInvoice, updateOrderItemsTracking, recheckOrderPaymentStatus } from '@/lib/api/ordersApi'
import { FaDownload, FaTruck, FaCreditCard, FaMapMarkerAlt, FaUser, FaEnvelope, FaPhone, FaSpinner, FaSync, FaArrowLeft, FaChevronRight, FaChevronDown, FaBoxOpen, FaUndo, FaTag, FaLayerGroup, FaSlidersH, FaImage, FaExternalLinkAlt } from 'react-icons/fa'

const OrderDetail = () => {
    const { orderId } = useParams()
    const navigate = useNavigate()

    const [order, setOrder] = useState(null)
    const [loading, setLoading] = useState(true)
    const [updating, setUpdating] = useState(false)
    const [downloading, setDownloading] = useState(false)
    const [emailing, setEmailing] = useState(false)
    const [newOrderStatus, setNewOrderStatus] = useState('')
    const [newPaymentStatus, setNewPaymentStatus] = useState('')
    const [selectedItem, setSelectedItem] = useState(null)
    const [showItemModal, setShowItemModal] = useState(false)
    const [editedItems, setEditedItems] = useState({})
    const [savingTracking, setSavingTracking] = useState(false)
    const [recheckingPayment, setRecheckingPayment] = useState(false)
    const [expandedItems, setExpandedItems] = useState({})

    // Modal active selection state
    const [activeComboSubIndex, setActiveComboSubIndex] = useState(null)
    const [activeModalImage, setActiveModalImage] = useState('')

    const fetchOrderDetails = useCallback(async () => {
        try {
            setLoading(true)
            const response = await getOrderDetails(orderId)
            if (response.success) {
                setOrder(response.data)
                setNewOrderStatus(response.data.orderStatus || '')
                setNewPaymentStatus(response.data.paymentStatus || '')
                const initial = {}
                if (response.data.items) {
                    response.data.items.forEach((it, idx) => {
                        initial[idx] = {
                            name: it.name,
                            variationName: it.variationName || '',
                            trackingCode: it.trackingCode || '',
                            deliveryCompany: it.deliveryCompany || '',
                            itemStatus: it.itemStatus || 'pending',
                            returnStatus: it.returnStatus || 'none',
                            returnType: it.returnType || 'refund',
                            returnTrackingCode: it.returnTrackingCode || '',
                            returnDeliveryCompany: it.returnDeliveryCompany || ''
                        }
                    })
                }
                setEditedItems(initial)
            } else {
                alert('Order not found')
                navigate('/orders/list')
            }
        } catch (error) {
            console.error('Error fetching order details:', error)
            alert('Failed to load order details')
            navigate('/orders/list')
        } finally {
            setLoading(false)
        }
    }, [orderId, navigate])

    useEffect(() => {
        if (orderId) {
            fetchOrderDetails()
        }
    }, [orderId, fetchOrderDetails])

    const handleUpdateOrderStatus = async () => {
        if (!newOrderStatus || newOrderStatus.toLowerCase() === (order.orderStatus?.toLowerCase())) return

        try {
            setUpdating(true)
            const response = await updateOrderStatus(orderId, newOrderStatus)
            if (response.success) {
                setOrder(prev => ({ ...prev, orderStatus: newOrderStatus }))
                alert('Order status updated successfully')
            } else {
                alert('Failed to update order status')
            }
        } catch (error) {
            console.error('Error updating order status:', error)
            alert('Failed to update order status')
        } finally {
            setUpdating(false)
        }
    }

    const handleUpdatePaymentStatus = async () => {
        if (!newPaymentStatus || newPaymentStatus.toLowerCase() === (order.paymentStatus?.toLowerCase())) return

        try {
            setUpdating(true)
            const response = await updatePaymentStatus(orderId, newPaymentStatus)
            if (response.success) {
                setOrder(prev => ({ ...prev, paymentStatus: newPaymentStatus }))
                alert('Payment status updated successfully')
            } else {
                alert('Failed to update payment status')
            }
        } catch (error) {
            console.error('Error updating payment status:', error)
            alert('Failed to update payment status')
        } finally {
            setUpdating(false)
        }
    }

    const handleRecheckPaymentStatus = async () => {
        try {
            setRecheckingPayment(true)
            const response = await recheckOrderPaymentStatus(orderId)
            if (response.success) {
                await fetchOrderDetails()
                if (response.updated) {
                    alert(`Payment status updated! New status: ${response.latestStatus.orderStatus}`)
                } else {
                    alert(`Payment status checked. Current status: ${response.latestStatus.orderStatus}\n${response.latestStatus.statusMessage}`)
                }
            } else {
                alert(response.message || 'Failed to check payment status')
            }
        } catch (error) {
            console.error('Error re-checking payment status:', error)
            alert(error.response?.data?.message || 'Failed to check payment status with PhonePe')
        } finally {
            setRecheckingPayment(false)
        }
    }

    const handleDownloadInvoice = async () => {
        try {
            setDownloading(true)
            await downloadInvoice(orderId)
            alert('Invoice download started')
        } catch (error) {
            console.error('Error downloading invoice:', error)
            alert('Failed to download invoice')
        } finally {
            setDownloading(false)
        }
    }

    const handleEmailInvoice = async () => {
        try {
            setEmailing(true)
            const response = await emailInvoice(orderId)
            if (response.success) {
                alert(`Invoice sent successfully to ${response.email}`)
            } else {
                alert(response.message || 'Failed to send invoice')
            }
        } catch (error) {
            console.error('Error emailing invoice:', error)
            alert('Failed to send invoice')
        } finally {
            setEmailing(false)
        }
    }

    const handleChangeItem = (index, field, value) => {
        setEditedItems(prev => ({
            ...prev,
            [index]: {
                ...prev[index],
                [field]: value
            }
        }))
    }

    const toggleExpandItem = (index) => {
        setExpandedItems(prev => ({
            ...prev,
            [index]: !prev[index]
        }))
    }

    const toggleExpandAll = () => {
        const allExpanded = order?.items?.every((_, idx) => expandedItems[idx])
        const nextState = {}
        order?.items?.forEach((_, idx) => {
            nextState[idx] = !allExpanded
        })
        setExpandedItems(nextState)
    }

    const handleSaveTracking = async () => {
        try {
            setSavingTracking(true)
            const payloadItems = Object.values(editedItems).map(it => ({
                name: it.name,
                variationName: it.variationName || undefined,
                trackingCode: it.trackingCode || null,
                deliveryCompany: it.deliveryCompany || null,
                itemStatus: it.itemStatus || undefined,
                returnStatus: it.returnStatus || undefined,
                returnType: it.returnType || undefined,
                returnTrackingCode: it.returnTrackingCode || undefined,
                returnDeliveryCompany: it.returnDeliveryCompany || undefined
            }))

            const response = await updateOrderItemsTracking(orderId, payloadItems)

            if (response.success) {
                setOrder(prev => ({
                    ...prev,
                    items: (prev.items || []).map((it, idx) => ({
                        ...it,
                        trackingCode: editedItems[idx]?.trackingCode || '',
                        deliveryCompany: editedItems[idx]?.deliveryCompany || '',
                        itemStatus: editedItems[idx]?.itemStatus || it.itemStatus,
                        returnStatus: editedItems[idx]?.returnStatus || it.returnStatus,
                        returnType: editedItems[idx]?.returnType || it.returnType,
                        returnTrackingCode: editedItems[idx]?.returnTrackingCode || it.returnTrackingCode,
                        returnDeliveryCompany: editedItems[idx]?.returnDeliveryCompany || it.returnDeliveryCompany
                    }))
                }))
                alert(`Tracking info saved successfully! Updated ${response.updatedCount || payloadItems.length} items.`)
            } else {
                alert(response.message || 'Failed to save tracking info')
            }
        } catch (error) {
            console.error('Error saving tracking:', error)
            alert(error.response?.data?.message || 'Failed to save tracking info.')
        } finally {
            setSavingTracking(false)
        }
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A'
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(price || 0)
    }

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'preparing': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
            case 'shipped': return 'bg-blue-100 text-blue-800 border-blue-200'
            case 'delivered': return 'bg-green-100 text-green-800 border-green-200'
            case 'cancelled': return 'bg-red-100 text-red-800 border-red-200'
            case 'returned': return 'bg-orange-100 text-orange-800 border-orange-200'
            default: return 'bg-gray-100 text-gray-800 border-gray-200'
        }
    }

    const getPaymentStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'successful': return 'bg-green-100 text-green-800 border-green-200'
            case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200'
            case 'failed': return 'bg-red-100 text-red-800 border-red-200'
            case 'refunded': return 'bg-purple-100 text-purple-800 border-purple-200'
            default: return 'bg-gray-100 text-gray-800 border-gray-200'
        }
    }

    const handleItemClick = (item) => {
        setSelectedItem(item)
        setActiveComboSubIndex(null)
        
        const mainImg = item.featuredImage?.[0]?.imgUrl || item.featuredImage?.[0] || '/placeholder-product.jpg'
        setActiveModalImage(mainImg)
        setShowItemModal(true)
    }

    const closeItemModal = () => {
        setShowItemModal(false)
        setSelectedItem(null)
        setActiveComboSubIndex(null)
        setActiveModalImage('')
    }

    const getItemImages = (item) => {
        if (!item) return []
        const imgs = []
        
        if (Array.isArray(item.featuredImage)) {
            item.featuredImage.forEach(img => {
                const url = typeof img === 'object' ? img.imgUrl : img
                if (url && !imgs.includes(url)) imgs.push(url)
            })
        } else if (typeof item.featuredImage === 'string') {
            imgs.push(item.featuredImage)
        }

        if (Array.isArray(item.galleryImage)) {
            item.galleryImage.forEach(img => {
                const url = typeof img === 'object' ? img.imgUrl : img
                if (url && !imgs.includes(url)) imgs.push(url)
            })
        }
        
        return imgs.length > 0 ? imgs : ['/placeholder-product.jpg']
    }

    // Helper to render capitalized variation badges with key: value attribute pairs
    const renderVariationAttributes = (target) => {
        if (!target) return null;
        const values = target.variationValues || target.variation?.variationValues;

        if (Array.isArray(values) && values.length > 0) {
            return (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {values.map((attr, idx) => {
                        if (typeof attr === 'object' && attr !== null) {
                            const entries = Object.entries(attr);
                            if (entries.length > 0) {
                                if (attr.label && attr.value) {
                                    return (
                                        <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-900 border border-blue-200 rounded text-[10px] font-extrabold uppercase tracking-wide">
                                            {String(attr.label).toUpperCase()}: <span className="text-blue-700 font-black">{String(attr.value).toUpperCase()}</span>
                                        </span>
                                    );
                                }
                                const [k, v] = entries[0];
                                return (
                                    <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-900 border border-blue-200 rounded text-[10px] font-extrabold uppercase tracking-wide">
                                        {String(k).toUpperCase()}: <span className="text-blue-700 font-black">{String(v).toUpperCase()}</span>
                                    </span>
                                );
                            }
                        }
                        return null;
                    })}
                </div>
            );
        }

        const varName = target.variationName || target.variation?.variationName;
        if (varName) {
            if (varName.includes('/')) {
                const parts = varName.split('/').map(p => p.trim());
                return (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {parts.map((p, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-900 border border-blue-200 rounded text-[10px] font-extrabold uppercase tracking-wide">
                                {p.toUpperCase()}
                            </span>
                        ))}
                    </div>
                );
            }
            return (
                <div className="mt-1.5">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-900 border border-blue-200 rounded text-[10px] font-extrabold uppercase tracking-wide">
                        OPTION: {varName.toUpperCase()}
                    </span>
                </div>
            );
        }

        return null;
    };

    // Helper to render customized product inputs with human-readable labels and image rendering
    const renderCustomInputs = (targetItem, isCompact = false) => {
        if (!targetItem?.custom_inputs) return null;
        let inputs = targetItem.custom_inputs;
        if (typeof inputs === 'string') {
            try { inputs = JSON.parse(inputs); } catch { return null; }
        }
        if (!inputs || typeof inputs !== 'object' || Object.keys(inputs).length === 0) return null;

        const defs = Array.isArray(targetItem.productCustomInputs) ? targetItem.productCustomInputs : [];

        const isImgUrl = (val, k) => {
            if (typeof val !== 'string') return false;
            const s = val.trim().toLowerCase();
            if (s.startsWith('http://') || s.startsWith('https://') || s.includes('b-cdn.net') || s.includes('cloudinary') || s.includes('/uploads/')) return true;
            if (/\.(png|jpg|jpeg|webp|gif)(\?.*)?$/i.test(s)) return true;
            if (k && k.toLowerCase().includes('image')) return true;
            return false;
        };

        const entries = Object.entries(inputs);

        if (isCompact) {
            return (
                <div className="mt-1 flex flex-wrap gap-1">
                    {entries.map(([key, val], idx) => {
                        const def = defs.find(d => String(d.id) === String(key) || String(d.name) === String(key) || String(d.label) === String(key));
                        let label = def?.label || def?.fieldName || def?.name;
                        if (!label) {
                            if (key.toLowerCase().includes('image')) label = 'Customer Image';
                            else if (/^\d{10,}$/.test(key)) label = `Option ${idx + 1}`;
                            else label = key.replace(/_/g, ' ');
                        }

                        const isImg = isImgUrl(val, key);

                        return (
                            <span key={key} className="px-1.5 py-0.5 bg-purple-50 text-purple-800 border border-purple-200 rounded text-[9px] font-bold uppercase tracking-tight flex items-center gap-1">
                                <span>{String(label).toUpperCase()}:</span>
                                {isImg ? (
                                    <a href={val} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="underline text-blue-600 font-extrabold flex items-center gap-0.5">
                                        📷 VIEW IMAGE ↗
                                    </a>
                                ) : (
                                    <span className="font-extrabold text-purple-950">{String(val).toUpperCase()}</span>
                                )}
                            </span>
                        );
                    })}
                </div>
            );
        }

        return (
            <div className="bg-purple-50/70 p-5 rounded-xl border border-purple-200 space-y-3">
                <div className="flex items-center gap-2 text-purple-900 font-bold text-xs uppercase tracking-wider">
                    <span className="w-2 h-2 rounded-full bg-purple-600"></span>
                    <span>Customized Product Customer Requirements</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {entries.map(([key, val], idx) => {
                        const def = defs.find(d => String(d.id) === String(key) || String(d.name) === String(key) || String(d.label) === String(key));
                        let label = def?.label || def?.fieldName || def?.name;
                        if (!label) {
                            if (key.toLowerCase().includes('image')) label = 'Customer Uploaded Image';
                            else if (/^\d{10,}$/.test(key)) label = `Custom Option ${idx + 1}`;
                            else label = key.replace(/_/g, ' ');
                        }

                        const isImg = isImgUrl(val, key);

                        return (
                            <div key={key} className="bg-white p-3.5 rounded-lg border border-purple-200 shadow-sm flex flex-col justify-between">
                                <div>
                                    <p className="text-[10px] font-extrabold text-purple-700 uppercase tracking-wider">{String(label).toUpperCase()}</p>
                                    {isImg ? (
                                        <div className="mt-2 space-y-2">
                                            <a href={val} target="_blank" rel="noreferrer" className="block relative w-32 h-32 rounded-lg overflow-hidden border border-purple-200 shadow hover:opacity-90 transition-opacity">
                                                <img src={val} alt="Customer Upload" className="w-full h-full object-cover" />
                                            </a>
                                            <a
                                                href={val}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-800 underline"
                                            >
                                                <span>Open Full Image ↗</span>
                                            </a>
                                        </div>
                                    ) : (
                                        <p className="text-xs font-bold text-gray-900 mt-1 uppercase break-words">{String(val).toUpperCase()}</p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <Layout active={'admin-orders-detail'} title={'Order Detail'}>
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-4 text-secondary-text">Loading order details...</p>
                    </div>
                </div>
            </Layout>
        )
    }

    if (!order) {
        return (
            <Layout active={'admin-orders-detail'} title={'Order Detail'}>
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <p className="text-red-600 text-lg">Order not found</p>
                        <button
                            onClick={() => navigate('/orders/list')}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Back to Orders
                        </button>
                    </div>
                </div>
            </Layout>
        )
    }

    const currentModalTarget = (selectedItem && activeComboSubIndex !== null && selectedItem.comboItems?.[activeComboSubIndex]) 
        ? selectedItem.comboItems[activeComboSubIndex] 
        : selectedItem

    const targetImages = getItemImages(currentModalTarget)

    return (
        <Layout active={'admin-orders-detail'} title={`Order Detail #${order.orderID}`}>
            <div className="flex flex-col gap-6 max-w-full">
                
                {/* Top Toolbar Header */}
                <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-wrap">
                        <button
                            onClick={() => navigate('/orders/list')}
                            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors"
                            title="Back to Orders"
                        >
                            <FaArrowLeft />
                        </button>
                        <div>
                            <div className="flex items-center gap-3">
                                <h1 className="text-xl font-bold text-gray-900">Order #{order.orderID}</h1>
                                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusColor(order.orderStatus)}`}>
                                    {order.orderStatus || 'N/A'}
                                </span>
                                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getPaymentStatusColor(order.paymentStatus)}`}>
                                    Payment: {order.paymentStatus || 'N/A'}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Placed on {formatDate(order.createdAt)} • Mode: <span className="font-semibold text-gray-700">{order.paymentMode}</span></p>
                        </div>
                    </div>

                    {/* Quick Action Buttons */}
                    <div className="flex items-center gap-2 flex-wrap w-full md:w-auto">
                        <button
                            onClick={handleDownloadInvoice}
                            disabled={downloading}
                            className="px-3 py-2 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-100 text-xs font-medium flex items-center gap-1.5 transition-colors disabled:opacity-50"
                        >
                            {downloading ? <FaSpinner className="animate-spin" /> : <FaDownload />}
                            <span>Download Invoice</span>
                        </button>
                        <button
                            onClick={handleEmailInvoice}
                            disabled={emailing}
                            className="px-3 py-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-100 text-xs font-medium flex items-center gap-1.5 transition-colors disabled:opacity-50"
                        >
                            {emailing ? <FaSpinner className="animate-spin" /> : <FaEnvelope />}
                            <span>Email Invoice</span>
                        </button>
                    </div>
                </div>

                {/* 3-Column Info Cards Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    
                    {/* Order Financial Summary Card */}
                    <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between">
                        <div>
                            <div className="flex items-center gap-2 pb-3 mb-3 border-b border-gray-100">
                                <FaCreditCard className="text-blue-500" />
                                <h3 className="font-semibold text-gray-900 text-sm">Financial Overview</h3>
                            </div>
                            <div className="space-y-2.5 text-xs">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-500">Total Paid:</span>
                                    <span className="font-bold text-gray-900 text-base">{formatPrice(order.total)}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-500">Payment Method:</span>
                                    <span className="font-medium text-gray-800">{order.paymentMode || 'N/A'}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-500">Payment Status:</span>
                                    <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${getPaymentStatusColor(order.paymentStatus)}`}>
                                        {order.paymentStatus || 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Customer Info Card */}
                    <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between">
                        <div>
                            <div className="flex items-center gap-2 pb-3 mb-3 border-b border-gray-100">
                                <FaUser className="text-blue-500" />
                                <h3 className="font-semibold text-gray-900 text-sm">Customer Details</h3>
                            </div>
                            <div className="space-y-2.5 text-xs">
                                <div className="flex items-center gap-2">
                                    <FaUser className="text-gray-400 text-xs shrink-0" />
                                    <span className="font-medium text-gray-900">{order.deliveryAddress?.name || order.deliveryAddress?.emailID || 'N/A'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <FaEnvelope className="text-gray-400 text-xs shrink-0" />
                                    <span className="text-gray-700 truncate">{order.deliveryAddress?.emailID || 'N/A'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <FaPhone className="text-gray-400 text-xs shrink-0" />
                                    <span className="text-gray-700">{order.deliveryAddress?.phoneNumber || 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Delivery Address Card */}
                    <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between">
                        <div>
                            <div className="flex items-center gap-2 pb-3 mb-3 border-b border-gray-100">
                                <FaMapMarkerAlt className="text-blue-500" />
                                <h3 className="font-semibold text-gray-900 text-sm">Delivery Address</h3>
                            </div>
                            {order.deliveryAddress ? (
                                <div className="text-xs text-gray-700 leading-relaxed space-y-0.5">
                                    <p className="font-semibold text-gray-900">{order.deliveryAddress.line1}</p>
                                    {order.deliveryAddress.line2 && <p>{order.deliveryAddress.line2}</p>}
                                    <p>{order.deliveryAddress.city}, {order.deliveryAddress.state} - <span className="font-medium">{order.deliveryAddress.pincode}</span></p>
                                    {order.deliveryAddress.landmark && (
                                        <p className="text-gray-500 text-[11px] mt-1">Landmark: {order.deliveryAddress.landmark}</p>
                                    )}
                                </div>
                            ) : (
                                <p className="text-xs text-gray-400">No address provided</p>
                            )}
                        </div>
                    </div>

                </div>

                {/* Status Update Controls Bar */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                    {/* Order Status Controller */}
                    <div className="space-y-3">
                        <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">Update Order Status</label>
                        <div className="flex items-center gap-2">
                            <Select value={newOrderStatus?.toLowerCase() || 'select'} onValueChange={(value) => setNewOrderStatus(value === 'select' ? '' : value)}>
                                <SelectTrigger className="bg-gray-50 border-gray-300">
                                    <SelectValue placeholder="Select new order status" />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                    <SelectItem value="select">Select new status</SelectItem>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="preparing">Preparing</SelectItem>
                                    <SelectItem value="shipped">Shipped</SelectItem>
                                    <SelectItem value="delivered">Delivered</SelectItem>
                                    <SelectItem value="returned">Returned</SelectItem>
                                    <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                            </Select>
                            <button
                                onClick={handleUpdateOrderStatus}
                                disabled={updating || !newOrderStatus || newOrderStatus === 'select' || newOrderStatus?.toLowerCase() === order.orderStatus?.toLowerCase()}
                                className="px-4 py-2 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed shrink-0 transition-colors"
                            >
                                {updating ? 'Updating...' : 'Update Status'}
                            </button>
                        </div>
                    </div>

                    {/* Payment Status Controller */}
                    <div className="space-y-3">
                        <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">Update Payment Status</label>
                        <div className="flex items-center gap-2">
                            <Select value={newPaymentStatus?.toLowerCase() || 'select'} onValueChange={(value) => setNewPaymentStatus(value === 'select' ? '' : value)}>
                                <SelectTrigger className="bg-gray-50 border-gray-300">
                                    <SelectValue placeholder="Select new payment status" />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                    <SelectItem value="select">Select new payment status</SelectItem>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="successful">Successful</SelectItem>
                                    <SelectItem value="failed">Failed</SelectItem>
                                    <SelectItem value="refunded">Refunded</SelectItem>
                                </SelectContent>
                            </Select>
                            <button
                                onClick={handleUpdatePaymentStatus}
                                disabled={updating || !newPaymentStatus || newPaymentStatus === 'select' || newPaymentStatus?.toLowerCase() === order.paymentStatus?.toLowerCase()}
                                className="px-4 py-2 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed shrink-0 transition-colors"
                            >
                                {updating ? 'Updating...' : 'Update Payment'}
                            </button>
                            {(order.paymentMode === 'PREPAID' || order.paymentMode === 'PHONEPE') && (
                                <button
                                    onClick={handleRecheckPaymentStatus}
                                    disabled={recheckingPayment}
                                    className="px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-100 flex items-center gap-1.5 shrink-0 transition-colors disabled:opacity-50"
                                    title="Re-check PhonePe Status"
                                >
                                    {recheckingPayment ? <FaSpinner className="animate-spin" /> : <FaSync />}
                                    <span>Verify</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Clean Compact Order Items Table Card */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-gray-50/50">
                        <div>
                            <h2 className="text-base font-bold text-gray-900">Order Items ({order.items?.length || 0})</h2>
                            <p className="text-xs text-gray-500 mt-0.5">Click any product name to view full images & attributes. Click <span className="font-semibold text-blue-600">‹ / ›</span> for logistics.</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={toggleExpandAll}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors bg-white"
                            >
                                {order?.items?.every((_, idx) => expandedItems[idx]) ? 'Collapse All Trays' : 'Expand All Trays'}
                            </button>
                            <button
                                onClick={handleSaveTracking}
                                disabled={savingTracking}
                                className={`px-4 py-2.5 rounded-lg flex items-center gap-2 text-xs font-semibold transition-all ${
                                    savingTracking
                                        ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                                        : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                                }`}
                            >
                                {savingTracking ? (
                                    <>
                                        <FaSpinner className="animate-spin" />
                                        <span>Saving Tracking...</span>
                                    </>
                                ) : (
                                    <>
                                        <FaTruck />
                                        <span>Save Tracking & Statuses</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto w-full">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-100/70 border-b border-gray-200 text-[11px] font-bold text-gray-700 uppercase tracking-wider">
                                    <th className="py-3.5 px-4">Product Details</th>
                                    <th className="py-3.5 px-3 text-center w-20">Qty</th>
                                    <th className="py-3.5 px-3 text-right w-28">Price</th>
                                    <th className="py-3.5 px-3 text-right w-32">Subtotal</th>
                                    <th className="py-3.5 px-4 text-center w-48">Logistics & Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-xs">
                                {order.items?.map((item, index) => {
                                    const isExpanded = !!expandedItems[index];
                                    const currentStatus = editedItems[index]?.itemStatus || item.itemStatus || 'pending';
                                    const trackingCode = editedItems[index]?.trackingCode || item.trackingCode;

                                    return (
                                        <React.Fragment key={index}>
                                            {/* Main Clean Row */}
                                            <tr className={`hover:bg-blue-50/20 transition-colors ${isExpanded ? 'bg-blue-50/30' : ''}`}>
                                                {/* Product Description */}
                                                <td className="py-4 px-4 align-middle">
                                                    <div className="flex items-center gap-3">
                                                        <img
                                                            src={item.featuredImage?.[0]?.imgUrl || item.featuredImage?.[0] || '/placeholder-product.jpg'}
                                                            alt={item.name}
                                                            className="w-12 h-12 object-cover rounded-lg border border-gray-200 shadow-sm shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                                                            onClick={() => handleItemClick(item)}
                                                            title="Click to view full product details"
                                                        />
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                                <button 
                                                                    className="font-bold text-gray-900 text-xs hover:text-blue-600 hover:underline transition-colors text-left"
                                                                    onClick={() => handleItemClick(item)}
                                                                >
                                                                    {item.name}
                                                                </button>
                                                                {item.comboItems && item.comboItems.length > 0 && (
                                                                    <span className="text-[9px] bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider border border-green-200">
                                                                        Combo ({item.comboItems.length})
                                                                    </span>
                                                                )}
                                                            </div>
                                                            
                                                            {/* Capitalized Variation & Attributes */}
                                                            {renderVariationAttributes(item)}

                                                            {/* Custom Inputs */}
                                                            {renderCustomInputs(item, true)}
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Quantity */}
                                                <td className="py-4 px-3 text-center align-middle">
                                                    <span className="inline-flex items-center justify-center px-2.5 py-1 bg-gray-100 border border-gray-200 rounded-md text-xs font-bold text-gray-800">
                                                        {item.quantity}
                                                    </span>
                                                </td>

                                                {/* Price */}
                                                <td className="py-4 px-3 text-right align-middle whitespace-nowrap text-xs text-gray-600">
                                                    {formatPrice(item.salePrice || item.regularPrice || 0)}
                                                </td>

                                                {/* Subtotal */}
                                                <td className="py-4 px-3 text-right align-middle whitespace-nowrap font-bold text-xs text-gray-900">
                                                    {formatPrice(item.lineTotalAfter || (item.salePrice || item.regularPrice || 0) * item.quantity)}
                                                </td>

                                                {/* Compact Action Column with Toggle Button */}
                                                <td className="py-4 px-4 text-center align-middle">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${
                                                            currentStatus === 'delivered' ? 'bg-green-100 text-green-700 border-green-200' :
                                                            currentStatus === 'shipped' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                                                            'bg-yellow-100 text-yellow-700 border-yellow-200'
                                                        }`}>
                                                            {currentStatus}
                                                        </span>

                                                        <button
                                                            onClick={() => toggleExpandItem(index)}
                                                            className={`px-3 py-1.5 rounded-lg border text-xs font-semibold flex items-center gap-1.5 transition-all shadow-sm ${
                                                                isExpanded 
                                                                    ? 'bg-blue-600 text-white border-blue-700 shadow' 
                                                                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                                            }`}
                                                            title={isExpanded ? "Close Action Tray" : "Open Action Tray"}
                                                        >
                                                            <span>{isExpanded ? 'Hide' : 'Manage'}</span>
                                                            <span className="text-xs">{isExpanded ? '‹' : '›'}</span>
                                                        </button>
                                                    </div>
                                                    {trackingCode && (
                                                        <p className="text-[10px] text-gray-400 mt-1 font-mono truncate max-w-[140px] mx-auto">
                                                            Trk: {trackingCode}
                                                        </p>
                                                    )}
                                                </td>
                                            </tr>

                                            {/* Expandable Action Tray Sub-Row */}
                                            {isExpanded && (
                                                <tr className="bg-slate-50/90 border-b border-gray-200">
                                                    <td colSpan={5} className="p-4">
                                                        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-6">
                                                            
                                                            {/* 1. Logistics / Tracking Panel */}
                                                            <div className="space-y-2 border-r md:border-r border-gray-100 pr-0 md:pr-4">
                                                                <div className="flex items-center gap-1.5 text-blue-600 font-bold text-xs uppercase tracking-wider">
                                                                    <FaTruck />
                                                                    <span>Logistics & Tracking</span>
                                                                </div>
                                                                <div className="space-y-2 pt-1">
                                                                    <div>
                                                                        <label className="text-[10px] font-semibold text-gray-500 uppercase block mb-0.5">Tracking Number</label>
                                                                        <input
                                                                            type="text"
                                                                            value={(editedItems[index]?.trackingCode || '')}
                                                                            onChange={(e) => handleChangeItem(index, 'trackingCode', e.target.value)}
                                                                            placeholder="Enter Tracking Code"
                                                                            className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label className="text-[10px] font-semibold text-gray-500 uppercase block mb-0.5">Courier Company</label>
                                                                        <input
                                                                            type="text"
                                                                            value={(editedItems[index]?.deliveryCompany || '')}
                                                                            onChange={(e) => handleChangeItem(index, 'deliveryCompany', e.target.value)}
                                                                            placeholder="e.g. Delhivery, Blue Dart"
                                                                            className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* 2. Item Status Selector */}
                                                            <div className="space-y-2 border-r md:border-r border-gray-100 pr-0 md:pr-4">
                                                                <div className="flex items-center gap-1.5 text-purple-600 font-bold text-xs uppercase tracking-wider">
                                                                    <FaBoxOpen />
                                                                    <span>Item Status</span>
                                                                </div>
                                                                <div className="pt-1">
                                                                    {(!item.brandID || item.brandID === 'inhouse' || item.brandName?.toLowerCase() === 'inhouse') ? (
                                                                        <div>
                                                                            <label className="text-[10px] font-semibold text-gray-500 uppercase block mb-0.5">Change Item Status</label>
                                                                            <select
                                                                                value={editedItems[index]?.itemStatus || 'pending'}
                                                                                onChange={(e) => handleChangeItem(index, 'itemStatus', e.target.value)}
                                                                                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs bg-white font-semibold uppercase tracking-wider focus:ring-blue-500"
                                                                            >
                                                                                <option value="pending">Pending</option>
                                                                                <option value="preparing">Preparing</option>
                                                                                <option value="shipped">Shipped</option>
                                                                                <option value="delivered">Delivered</option>
                                                                            </select>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-xs">
                                                                            <span className="text-gray-500 font-medium">Brand Status: </span>
                                                                            <span className="font-bold text-gray-900 uppercase">{item.itemStatus || 'Pending'}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* 3. Return Management Panel */}
                                                            <div className="space-y-2">
                                                                <div className="flex items-center gap-1.5 text-orange-600 font-bold text-xs uppercase tracking-wider">
                                                                    <FaUndo />
                                                                    <span>Return Management</span>
                                                                </div>
                                                                <div className="pt-1">
                                                                    {['return_approval', 'refund_approval', 'replacement_approval'].includes(item.returnStatus) ? (
                                                                        <div className="bg-red-50 p-2.5 rounded-lg border border-red-200">
                                                                            <span className="text-[10px] font-bold text-red-700 uppercase tracking-wider block animate-pulse">
                                                                                {item.returnStatus.replace('_', ' ')}
                                                                            </span>
                                                                            <p className="text-[10px] text-gray-500 mt-0.5">Wait for Admin Approval in Return Queries</p>
                                                                        </div>
                                                                    ) : (!item.brandID || item.brandID === 'inhouse' || item.brandName?.toLowerCase() === 'inhouse') ? (
                                                                        <div>
                                                                            {(editedItems[index]?.returnStatus && !['none', 'returnRejected'].includes(editedItems[index]?.returnStatus)) ? (
                                                                                <div className="space-y-2">
                                                                                    <div className="grid grid-cols-2 gap-2">
                                                                                        <div>
                                                                                            <label className="text-[9px] text-gray-500 font-bold uppercase block mb-0.5">Type</label>
                                                                                            <select
                                                                                                value={editedItems[index]?.returnType || 'refund'}
                                                                                                onChange={(e) => handleChangeItem(index, 'returnType', e.target.value)}
                                                                                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs bg-blue-50 font-bold uppercase"
                                                                                            >
                                                                                                <option value="refund">REFUND</option>
                                                                                                <option value="replacement">REPLACEMENT</option>
                                                                                            </select>
                                                                                        </div>
                                                                                        <div>
                                                                                            <label className="text-[9px] text-gray-500 font-bold uppercase block mb-0.5">Return Status</label>
                                                                                            <select
                                                                                                value={editedItems[index]?.returnStatus}
                                                                                                onChange={(e) => handleChangeItem(index, 'returnStatus', e.target.value)}
                                                                                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs bg-white font-semibold uppercase"
                                                                                            >
                                                                                                <option value="return_initiated">Return initiated</option>
                                                                                                <option value="return_picked">Return picked</option>
                                                                                                <option value="returned">Returned</option>
                                                                                                {(editedItems[index]?.returnType || item.returnType) === 'replacement' ? (
                                                                                                    <>
                                                                                                        <option value="replacement_processing">Replacement processing</option>
                                                                                                        <option value="replacement_shipped">Replacement pending</option>
                                                                                                        <option value="replacement_complete">Replacement complete</option>
                                                                                                    </>
                                                                                                ) : (
                                                                                                    <>
                                                                                                        <option value="refund_pending">Refund pending</option>
                                                                                                        <option value="refund_completed">Refund completed</option>
                                                                                                    </>
                                                                                                )}
                                                                                                <option value="returnRejected">Return Rejected</option>
                                                                                            </select>
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="grid grid-cols-2 gap-2">
                                                                                        <input
                                                                                            type="text"
                                                                                            value={(editedItems[index]?.returnTrackingCode || '')}
                                                                                            onChange={(e) => handleChangeItem(index, 'returnTrackingCode', e.target.value)}
                                                                                            placeholder="Return Tracking #"
                                                                                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                                                                        />
                                                                                        <select
                                                                                            value={editedItems[index]?.returnDeliveryCompany || ''}
                                                                                            onChange={(e) => handleChangeItem(index, 'returnDeliveryCompany', e.target.value)}
                                                                                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                                                                                        >
                                                                                            <option value="">Courier</option>
                                                                                            <option value="Delhivery">Delhivery</option>
                                                                                            <option value="BlueDart">Blue Dart</option>
                                                                                            <option value="DTDC">DTDC</option>
                                                                                            <option value="Other">Other</option>
                                                                                        </select>
                                                                                    </div>
                                                                                </div>
                                                                            ) : (
                                                                                (editedItems[index]?.itemStatus === 'delivered') ? (
                                                                                    <button
                                                                                        onClick={() => handleChangeItem(index, 'returnStatus', 'return_initiated')}
                                                                                        className="w-full py-1.5 bg-orange-600 text-white rounded text-xs font-semibold hover:bg-orange-700 transition-colors uppercase tracking-wider"
                                                                                    >
                                                                                        Initiate Return Process
                                                                                    </button>
                                                                                ) : (
                                                                                    <p className="text-xs text-gray-400 italic">No return action available</p>
                                                                                )
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <p className="text-xs text-gray-400 italic">No return info</p>
                                                                    )}
                                                                </div>
                                                            </div>

                                                        </div>
                                                    </td>
                                                </tr>
                                            )}

                                            {/* Combo Items Rows */}
                                            {item.comboItems && item.comboItems.length > 0 && item.comboItems.map((comboItem, comboIndex) => (
                                                <tr key={`${index}-combo-${comboIndex}`} className="bg-gray-50/60 border-b border-gray-100 text-xs">
                                                    <td className="py-2.5 px-4 pl-10">
                                                        <div className="flex items-center gap-3">
                                                            <img
                                                                src={comboItem.featuredImage?.[0]?.imgUrl || comboItem.featuredImage?.[0] || '/placeholder-product.jpg'}
                                                                alt={comboItem.name}
                                                                className="w-9 h-9 object-cover rounded border border-gray-200 cursor-pointer"
                                                                onClick={() => {
                                                                    setSelectedItem(item)
                                                                    setActiveComboSubIndex(comboIndex)
                                                                    setShowItemModal(true)
                                                                }}
                                                            />
                                                            <div>
                                                                <button 
                                                                    className="font-medium text-gray-800 text-xs hover:text-blue-600 hover:underline text-left"
                                                                    onClick={() => {
                                                                        setSelectedItem(item)
                                                                        setActiveComboSubIndex(comboIndex)
                                                                        setShowItemModal(true)
                                                                    }}
                                                                >
                                                                    {comboItem.name}
                                                                </button>
                                                                {comboItem.brandName && (
                                                                    <p className="text-[10px] text-blue-600 font-semibold">{comboItem.brandName}</p>
                                                                )}
                                                                {renderVariationAttributes(comboItem)}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="py-2.5 px-3 text-center text-gray-500 text-xs">Included</td>
                                                    <td className="py-2.5 px-3 text-right text-gray-400 text-xs">-</td>
                                                    <td className="py-2.5 px-3 text-right text-gray-400 text-xs">-</td>
                                                    <td className="py-2.5 px-4 text-gray-400 text-xs text-center">-</td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Order Financial Breakdown Summary */}
                    <div className="p-5 bg-gray-50/50 border-t border-gray-200 flex justify-end">
                        <div className="w-full sm:w-80 space-y-2 text-xs">
                            <div className="flex justify-between text-gray-600">
                                <span>Subtotal:</span>
                                <span className="font-semibold text-gray-900">{formatPrice(order.subtotal)}</span>
                            </div>
                            {order.discount > 0 && (
                                <div className="flex justify-between text-emerald-600 font-medium">
                                    <span>Discount:</span>
                                    <span>-{formatPrice(order.discount)}</span>
                                </div>
                            )}
                            {order.couponDiscount > 0 && (
                                <div className="flex justify-between text-emerald-600 font-medium">
                                    <span>Coupon ({order.couponCode}):</span>
                                    <span>-{formatPrice(order.couponDiscount)}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-gray-600">
                                <span>Shipping:</span>
                                <span className="font-semibold text-gray-900">{formatPrice(order.shipping || 0)}</span>
                            </div>
                            {order.handlingFee && order.handFeeRate > 0 && (
                                <div className="flex justify-between text-gray-600">
                                    <span>Handling Fee:</span>
                                    <span className="font-semibold text-gray-900">{formatPrice(order.handFeeRate)}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-sm font-bold text-gray-900 border-t border-gray-200 pt-2.5">
                                <span>Final Order Total:</span>
                                <span className="text-base text-blue-700">{formatPrice(order.total)}</span>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* Rich Interactive Product Detail Modal */}
            {showItemModal && selectedItem && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-2xl flex flex-col">
                        
                        {/* Modal Header */}
                        <div className="p-5 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
                            <div className="flex items-center gap-2">
                                <h2 className="text-lg font-bold text-gray-900">Product Specification & Details</h2>
                                {selectedItem.comboItems && selectedItem.comboItems.length > 0 && (
                                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-800 border border-green-200">
                                        Combo Product ({selectedItem.comboItems.length} items)
                                    </span>
                                )}
                            </div>
                            <button
                                onClick={closeItemModal}
                                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-900 flex items-center justify-center text-lg font-bold transition-colors"
                            >
                                ×
                            </button>
                        </div>

                        <div className="p-6 space-y-6">

                            {/* Combo / Make Combo Sub-Product Selector Bar */}
                            {selectedItem.comboItems && selectedItem.comboItems.length > 0 && (
                                <div className="bg-emerald-50/70 p-4 rounded-xl border border-emerald-200">
                                    <div className="flex items-center gap-2 mb-3">
                                        <FaLayerGroup className="text-emerald-700 text-sm" />
                                        <h3 className="text-xs font-bold text-emerald-900 uppercase tracking-wider">
                                            Select Included Combo Item to Preview
                                        </h3>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                        {/* Main Parent Item Button */}
                                        <button
                                            onClick={() => {
                                                setActiveComboSubIndex(null)
                                                setActiveModalImage(getItemImages(selectedItem)[0])
                                            }}
                                            className={`p-2.5 rounded-lg border text-left flex items-center gap-2 transition-all ${
                                                activeComboSubIndex === null 
                                                    ? 'bg-emerald-700 text-white border-emerald-800 shadow-sm font-bold' 
                                                    : 'bg-white text-gray-700 border-emerald-200 hover:bg-emerald-100/50'
                                            }`}
                                        >
                                            <img
                                                src={getItemImages(selectedItem)[0]}
                                                alt={selectedItem.name}
                                                className="w-8 h-8 object-cover rounded shrink-0 border border-white/20"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <p className="text-[11px] truncate font-semibold">Main Bundle Product</p>
                                                <p className="text-[9px] opacity-80">Full Bundle Info</p>
                                            </div>
                                        </button>

                                        {/* Sub Products Buttons */}
                                        {selectedItem.comboItems.map((cItem, cIdx) => {
                                            const subImg = getItemImages(cItem)[0]
                                            const isSelected = activeComboSubIndex === cIdx

                                            return (
                                                <button
                                                    key={cIdx}
                                                    onClick={() => {
                                                        setActiveComboSubIndex(cIdx)
                                                        setActiveModalImage(subImg)
                                                    }}
                                                    className={`p-2.5 rounded-lg border text-left flex items-center gap-2 transition-all ${
                                                        isSelected 
                                                            ? 'bg-emerald-700 text-white border-emerald-800 shadow-sm font-bold' 
                                                            : 'bg-white text-gray-700 border-emerald-200 hover:bg-emerald-100/50'
                                                    }`}
                                                >
                                                    <img
                                                        src={subImg}
                                                        alt={cItem.name}
                                                        className="w-8 h-8 object-cover rounded shrink-0 border border-white/20"
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-[11px] truncate font-semibold">{cItem.name}</p>
                                                        <p className="text-[9px] opacity-80">{cItem.brandName || 'Combo Item'}</p>
                                                    </div>
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Main Product Info & Gallery Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                                
                                {/* Left Gallery Section (5 Columns) */}
                                <div className="md:col-span-5 space-y-3">
                                    <div className="relative aspect-square w-full rounded-xl border border-gray-200 overflow-hidden bg-gray-50 shadow-inner">
                                        <img
                                            src={activeModalImage || targetImages[0]}
                                            alt={currentModalTarget?.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>

                                    {/* Gallery Thumbnails Carousel/Grid */}
                                    {targetImages.length > 1 && (
                                        <div>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase mb-1.5 flex items-center gap-1">
                                                <FaImage /> Product Gallery ({targetImages.length})
                                            </p>
                                            <div className="flex gap-2 overflow-x-auto pb-1">
                                                {targetImages.map((imgUrl, imgIdx) => (
                                                    <button
                                                        key={imgIdx}
                                                        onClick={() => setActiveModalImage(imgUrl)}
                                                        className={`w-14 h-14 rounded-lg border-2 overflow-hidden shrink-0 transition-all ${
                                                            (activeModalImage || targetImages[0]) === imgUrl 
                                                                ? 'border-blue-600 shadow' 
                                                                : 'border-gray-200 hover:border-gray-300 opacity-70 hover:opacity-100'
                                                        }`}
                                                    >
                                                        <img src={imgUrl} alt="Thumbnail" className="w-full h-full object-cover" />
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Right Details Section (7 Columns) */}
                                <div className="md:col-span-7 space-y-4">
                                    <div>
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded">
                                            {currentModalTarget?.brandName || 'Ithyaraa Product'}
                                        </span>
                                        <h3 className="text-lg font-bold text-gray-900 mt-1.5 leading-snug">{currentModalTarget?.name}</h3>
                                        {currentModalTarget?.categories && (
                                            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                                                <FaTag className="text-gray-400 text-xs" />
                                                <span className="text-xs text-gray-500 font-semibold uppercase">Categories:</span>
                                                {Array.isArray(currentModalTarget.categories) ? (
                                                    currentModalTarget.categories.map((cat, idx) => (
                                                        <span key={idx} className="bg-gray-100 text-gray-800 px-2 py-0.5 rounded text-[11px] font-bold uppercase">
                                                            {typeof cat === 'object' ? cat.name : cat}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="bg-gray-100 text-gray-800 px-2 py-0.5 rounded text-[11px] font-bold uppercase">
                                                        {currentModalTarget.categories}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Pricing & Order Info */}
                                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-2 text-xs">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 font-medium">Order Quantity:</span>
                                            <span className="font-bold text-gray-900">{selectedItem.quantity} unit(s)</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-500 font-medium">Unit Sale Price:</span>
                                            <span className="font-bold text-blue-700 text-sm">{formatPrice(currentModalTarget?.salePrice || currentModalTarget?.regularPrice || selectedItem.salePrice || 0)}</span>
                                        </div>
                                        {currentModalTarget?.regularPrice && currentModalTarget?.regularPrice > (currentModalTarget?.salePrice || 0) && (
                                            <div className="flex justify-between items-center text-gray-400 line-through">
                                                <span>Regular Price:</span>
                                                <span>{formatPrice(currentModalTarget.regularPrice)}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Prominently Capitalized Variation & Attribute Badges */}
                                    <div className="bg-blue-50/70 p-4 rounded-xl border border-blue-200 space-y-2 text-xs">
                                        <div className="flex items-center gap-1.5 text-blue-800 font-bold uppercase tracking-wider text-[11px]">
                                            <FaSlidersH />
                                            <span>Selected Variation & Attributes</span>
                                        </div>
                                        {renderVariationAttributes(currentModalTarget)}
                                    </div>

                                </div>
                            </div>

                            {/* Customized Product Inputs Details with Image rendering & human labels */}
                            {renderCustomInputs(selectedItem, false)}

                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end sticky bottom-0 rounded-b-2xl">
                            <button
                                onClick={closeItemModal}
                                className="px-6 py-2 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-gray-800 transition-colors"
                            >
                                Close Details
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </Layout>
    )
}

export default OrderDetail
